import type { FC } from 'react';
import { memo, useCallback, useMemo, useState } from 'react';
import {
  Box,
  Card,
  Checkbox as MuiCheckbox,
  Collapse,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import type { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import { DataGrid } from '@mui/x-data-grid';
import type { HypothesisCreativeEntry, HypothesisSection } from '../../types/hypothesis';

interface HypothesisGroupCardProps {
  hypothesis: string;
  creatives: HypothesisCreativeEntry[];
  section: HypothesisSection | null;
  showSectionSelector: boolean;
  selectedIds: string[];
  columns: GridColDef[];
  onSelectionChange: (hypothesis: string, ids: string[]) => void;
  onSectionChange?: (hypothesis: string, section: HypothesisSection) => void;
}

const HypothesisGroupCardImpl: FC<HypothesisGroupCardProps> = ({
  hypothesis,
  creatives,
  section,
  showSectionSelector,
  selectedIds,
  columns,
  onSelectionChange,
  onSectionChange,
}) => {
  const [expanded, setExpanded] = useState(true);

  const groupCreativeIds = useMemo(
    () => creatives.map((c) => c.id),
    [creatives],
  );

  const allSelected = selectedIds.length === creatives.length && creatives.length > 0;
  const someSelected = selectedIds.length > 0 && !allSelected;

  const handleSelectAll = useCallback(() => {
    onSelectionChange(hypothesis, allSelected ? [] : groupCreativeIds);
  }, [allSelected, groupCreativeIds, hypothesis, onSelectionChange]);

  const handleRowSelectionChange = useCallback(
    (model: GridRowSelectionModel) => {
      onSelectionChange(hypothesis, model.map(String));
    },
    [hypothesis, onSelectionChange],
  );

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        boxShadow: 'none',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pl: .75,
          pr: 1.5,
          py: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <MuiCheckbox
            size="medium"
            checked={allSelected}
            indeterminate={someSelected}
            onChange={handleSelectAll}
            onClick={(e) => { e.stopPropagation(); }}
          />
          <IconButton
            size="small"
            onClick={() => { setExpanded((prev) => !prev); }}
          >
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
          <Typography
            variant="subtitle2"
            fontWeight={600}
            sx={{ cursor: 'pointer' }}
            onClick={() => { setExpanded((prev) => !prev); }}
          >
            {hypothesis}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
            (Creatives: {creatives.length})
          </Typography>
        </Box>
        {showSectionSelector && onSectionChange && (
          <FormControl size="small" variant="outlined">
            <Select
              value={section ?? 'upcoming'}
              onChange={(e) => {
                onSectionChange(hypothesis, e.target.value as HypothesisSection);
              }}
              size="small"
              sx={{ height: 32, minWidth: 120, fontSize: '0.875rem' }}
            >
              <MenuItem value="this_week">This week</MenuItem>
              <MenuItem value="upcoming">Upcoming</MenuItem>
            </Select>
          </FormControl>
        )}
      </Box>
      <Collapse in={expanded} unmountOnExit>
        <DataGrid
          rows={creatives}
          columns={columns}
          checkboxSelection
          rowSelectionModel={selectedIds}
          onRowSelectionModelChange={handleRowSelectionChange}
          keepNonExistentRowsSelected
          hideFooterPagination
          hideFooter
          disableColumnMenu
          rowHeight={60}
          getRowId={(row: HypothesisCreativeEntry) => row.id}
          sx={{
            border: 'none',
            '& .MuiDataGrid-cell': {
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiDataGrid-columnHeaders': {
              borderTop: 1,
              borderColor: 'divider',
              bgcolor: 'transparent',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontSize: '0.75rem',
              fontWeight: 600,
              color: 'text.secondary',
            },
          }}
        />
      </Collapse>
    </Card>
  );
};

const HypothesisGroupCard = memo(HypothesisGroupCardImpl);

export default HypothesisGroupCard;
